import * as React from "react";
import { cn } from "../lib/cn.js";

/**
 * Low-level shadcn `Table` primitives. These are the building blocks
 * for both `<DataTable>` (the opinionated Iron Rule #16 wrapper for
 * row-of-data lists) and any one-off table the app may need.
 *
 * IMPORTANT: app code should NOT import these directly for any list
 * of >5 rows of structured data — use `<DataTable>` instead so the
 * sticky-head / footer / toolbar contract from Iron Rule #16 is
 * enforced. See `.windsurf/rules/iron-rules.md` §16.
 *
 * Source baseline: shadcn/ui canonical `table.tsx` (2025), with two
 * deliberate departures:
 *  1. The outer wrapper `<div>` is omitted — `<DataTable>` owns the
 *     scroll container so it can pin sticky regions correctly.
 *  2. `TableRow` hover uses `bg-muted/40` (not `/50`) per Iron Rule
 *     #16.3 — softer alternation read better in the user-research
 *     refs (Pencil & Paper, Lollypop).
 */

const Table = React.forwardRef<HTMLTableElement, React.ComponentPropsWithoutRef<"table">>(
  ({ className, ...props }, ref) => (
    <table
      ref={ref}
      data-slot="table"
      className={cn("w-full caption-bottom border-separate border-spacing-0 text-sm", className)}
      {...props}
    />
  ),
);
Table.displayName = "Table";

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.ComponentPropsWithoutRef<"thead">
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    data-slot="table-header"
    className={cn(
      // Sticky to the *scroll container* (DataTableScroll), not the
      // page. `top-0` here means "pinned to the top of the scroll
      // viewport" — the parent overflow handles the rest.
      //
      // The bg MUST be FULLY OPAQUE: rows scroll BEHIND the sticky
      // thead, and any alpha < 1 lets row text bleed through (we
      // shipped 1 round with `bg-muted/40` and saw exactly that
      // bug on stage). `bg-muted` is the solid muted shade and
      // matches the tinted-head look from the user-research refs.
      "bg-muted text-muted-foreground sticky top-0 z-10",
      "[&_tr]:border-0",
      // Hairline below the head that matches the row dividers.
      "[&_th]:border-border/60 [&_th]:border-b",
      className,
    )}
    {...props}
  />
));
TableHeader.displayName = "TableHeader";

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.ComponentPropsWithoutRef<"tbody">
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    data-slot="table-body"
    className={cn(
      // Per Iron Rule #16.3: zebra stripes are banned. Use hairlines
      // between rows only.
      "[&>tr]:border-border/60 [&>tr:last-child]:border-b-0 [&>tr]:border-b",
      className,
    )}
    {...props}
  />
));
TableBody.displayName = "TableBody";

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.ComponentPropsWithoutRef<"tfoot">
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    data-slot="table-footer"
    className={cn("bg-muted/30 border-border/60 sticky bottom-0 border-t font-medium", className)}
    {...props}
  />
));
TableFooter.displayName = "TableFooter";

const TableRow = React.forwardRef<HTMLTableRowElement, React.ComponentPropsWithoutRef<"tr">>(
  ({ className, ...props }, ref) => (
    <tr
      ref={ref}
      data-slot="table-row"
      className={cn(
        // Iron Rule #16.5: the ONLY transition on a row is
        // background-color, 120ms ease-out. No transform, no shadow,
        // nothing else.
        "data-[state=selected]:bg-muted/60 transition-colors duration-150 ease-out",
        "hover:bg-muted/40",
        className,
      )}
      {...props}
    />
  ),
);
TableRow.displayName = "TableRow";

const TableHead = React.forwardRef<HTMLTableCellElement, React.ComponentPropsWithoutRef<"th">>(
  ({ className, ...props }, ref) => (
    <th
      ref={ref}
      data-slot="table-head"
      className={cn(
        // Compact heading row — 40px tall, generous horizontal padding
        // so column titles breathe. Uppercase + tracking matches the
        // ref images (Shadcnblocks Admin Kit User List).
        "text-muted-foreground h-10 whitespace-nowrap px-4 text-left align-middle text-xs font-medium uppercase tracking-wide",
        "[&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
        className,
      )}
      {...props}
    />
  ),
);
TableHead.displayName = "TableHead";

const TableCell = React.forwardRef<HTMLTableCellElement, React.ComponentPropsWithoutRef<"td">>(
  ({ className, ...props }, ref) => (
    <td
      ref={ref}
      data-slot="table-cell"
      className={cn(
        // Comfortable density (Iron Rule #16.3 default) = 56px row.
        // 16px vertical padding × 2 + ~24px line-height ≈ 56px.
        "px-4 py-3 align-middle",
        "[&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
        className,
      )}
      {...props}
    />
  ),
);
TableCell.displayName = "TableCell";

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.ComponentPropsWithoutRef<"caption">
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    data-slot="table-caption"
    // Iron Rule #16.6: caption is mandatory for a11y, but it's
    // visually redundant when a PageHeaderTitle already names the
    // table. Use `sr-only` in those cases.
    className={cn("text-muted-foreground mt-4 text-sm", className)}
    {...props}
  />
));
TableCaption.displayName = "TableCaption";

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption };
